import React, { Fragment, useEffect, useState } from "react";
import * as Styled from "./style";
import calendar, {
  isDate,
  isSameDay,
  isSameMonth,
  getNextMonth,
  getPreviousMonth,
  WEEK_DAYS,
  CALENDAR_MONTHS,
  isSameYear,
  getYearsRange
} from "./dateUtils";
import moment from "jalali-moment";
moment.locale("fa");

const CALENDAR_STATES = {
  DAY: "DAY",
  MONTH: "MONTH",
  YEAR: "YEAR"
};
export default function Calendar({ date, onDateChanged, name }) {
  const [dateState, setDateState] = useState({
    current: moment(),
    month: 0,
    year: 0
  });
  const [today] = useState(new moment());
  const [calendarState, setCalendarState] = useState(CALENDAR_STATES.DAY);
  const scrollableContainer = document.getElementById(
    `scrollable-container-${name}`
  );
  useEffect(() => {
    addDateToState(date);
  }, []);

  const addDateToState = date => {
    const isDateObject = isDate(date);
    const _date = isDateObject ? date : new moment();
    setDateState({
      current: isDateObject ? date : null,
      month: +_date.month() + 1,
      year: _date.year()
    });
  };
  const getCalendarDates = () => {
    const { current, month, year } = dateState;
    const calendarMonth = month || +current.month() + 1;
    const calendarYear = year || current.year();
    return calendar(calendarMonth, calendarYear);
  };

  const renderMonthAndYear = hideMonthShiftArrow => {
    const { month, year } = dateState;
    // Resolve the month name from the CALENDAR_MONTHS object map
    const monthName = CALENDAR_MONTHS[Object.keys(CALENDAR_MONTHS)[month - 1]];
    return (
      <Styled.CalendarHeader>
        {!hideMonthShiftArrow && (
          <Fragment>
            <Styled.ArrowLeft
              className="icon mapsa-left-arrow-chevron"
              onClick={handleNext}
              title="ماه بعدی"
            />
            <Styled.ArrowRight
              className="icon mapsa-forward"
              onClick={handlePrevious}
              title="ماه قبلی"
            />
          </Fragment>
        )}
        <Styled.CalendarSelectedMonthYear>
          {monthName} {year}
          <Styled.ArrowDown
            className="icon mapsa-down-arrow"
            onClick={changeCalendarState}
          />
        </Styled.CalendarSelectedMonthYear>
      </Styled.CalendarHeader>
    );
  };
  // Render the label for day of the week
  // This method is used as a map callback as seen in render()
  const renderDayLabel = (dayKey, index) => {
    // Resolve the day of the week label from the WEEK_DAYS object map
    const dayLabel = WEEK_DAYS[dayKey].toUpperCase();
    return (
      <Styled.CalendarDayCell key={dayLabel} index={index}>
        {dayLabel}
      </Styled.CalendarDayCell>
    );
  };
  // Render a calendar date as returned from the calendar builder function
  // This method is used as a map callback as seen in render()
  const renderCalendarDate = (passedDate, index) => {
    const { current, month, year } = dateState;
    const _date = new moment(passedDate.join("/"));
    // Check if calendar date is same day as today
    const isToday = isSameDay(_date, today);
    // Check if calendar date is same day as currently selected date
    const isCurrent = current && isSameDay(_date, current);
    // Check if calendar date is in the same month as the state month and year
    const inMonth =
      month &&
      year &&
      isSameMonth(_date, new moment([year, month, 1].join("/")));
    // The click handler
    const onClick = gotoDate(_date, true);
    const props = { index, inMonth, onClick };
    // Conditionally render a styled date componentDate
    const DateComponent = isCurrent
      ? Styled.HighlightedCalendarDate
      : isToday
      ? Styled.TodayCalendarDate
      : Styled.CalendarDate;

    return (
      <DateComponent key={index} {...props}>
        {_date.date()}
      </DateComponent>
    );
  };
  // Render a range of years
  // This method is used as a map callback as seen in render()
  const renderCalendarYears = (_year, index) => {
    const { year, month, current } = dateState;
    // The click handler
    const onClick = env => {
      gotoDate(moment([_year, month, current.date()].join("/")))(env);
      changeCalendarState(env, true);
    };
    const props = { index, onClick };
    const YearComponent = isSameYear(_year, year)
      ? Styled.HighlightedCalendarYear
      : Styled.CalendarYearCell;
    return (
      <YearComponent key={index} {...props}>
        {_year}
      </YearComponent>
    );
  };

  // Render a months
  // This method is used as a map callback as seen in render()
  const renderCalendarMonths = (monthKey, index) => {
    // Resolve the month label from the CALENDAR_MONTHS object map
    const monthLabel = CALENDAR_MONTHS[monthKey];
    const { year, month, current } = dateState;
    // The click handler
    const onClick = env => {
      gotoDate(moment([year, index + 1, current.date()].join("/")))(env);
      changeCalendarState(env);
    };
    const props = { index, onClick };
    const MonthComponent =
      month === index + 1
        ? Styled.HighlightedCalendarMonth
        : Styled.CalendarMonthCell;
    return (
      <MonthComponent key={index} {...props}>
        {monthLabel}
      </MonthComponent>
    );
  };

  const gotoDate = (date, close) => evt => {
    evt && evt.preventDefault();
    const { current } = dateState;
    !(current && isSameDay(date, current)) && addDateToState(date);
    if (close) {
      onDateChanged(date, close);
    }
  };
  const gotoPreviousMonth = () => {
    const { month, year } = dateState;
    const previousMonth = getPreviousMonth(month, year);
    setDateState({
      month: previousMonth.month,
      year: previousMonth.year,
      current: dateState.current
    });
  };
  const gotoNextMonth = () => {
    const { month, year } = dateState;
    const nextMonth = getNextMonth(month, year);
    setDateState({
      month: nextMonth.month,
      year: nextMonth.year,
      current: dateState.current
    });
  };
  const scrollToSelectedYear = () => {
    setTimeout(() => {
      const { year } = dateState;
      const yearsRangeArray = getYearsRange();
      const index = yearsRangeArray.findIndex(item => item === year);
      const rowNum = Math.floor(index / 4); // 4: #years in a row,
      scrollableContainer.scroll({
        top: (rowNum > 1 ? rowNum + 1 : rowNum) * 32, //32: yearCell height
        behavior: "auto"
      });
    }, 10);
  };
  const changeCalendarState = (evt, goToMonth) => {
    evt.stopPropagation();
    const newState =
      calendarState === CALENDAR_STATES.DAY
        ? CALENDAR_STATES.YEAR
        : goToMonth
        ? CALENDAR_STATES.MONTH
        : CALENDAR_STATES.DAY;
    //scroll to current year
    if (newState === CALENDAR_STATES.YEAR) {
      scrollToSelectedYear();
    }
    setCalendarState(newState);
  };

  const handlePrevious = evt => {
    evt.stopPropagation();
    gotoPreviousMonth();
  };
  const handleNext = evt => {
    evt.stopPropagation();
    gotoNextMonth();
  };
  return (
    <Styled.CalendarContainer>
      {renderMonthAndYear(calendarState !== CALENDAR_STATES.DAY)}
      <Styled.CalendarGrid id={`scrollable-container-${name}`}>
        {calendarState === CALENDAR_STATES.DAY && (
          <Fragment>
            <Fragment>{Object.keys(WEEK_DAYS).map(renderDayLabel)}</Fragment>
            <Fragment>{getCalendarDates().map(renderCalendarDate)}</Fragment>
          </Fragment>
        )}
        {calendarState === CALENDAR_STATES.YEAR && (
          <Fragment>{getYearsRange().map(renderCalendarYears)}</Fragment>
        )}
        {calendarState === CALENDAR_STATES.MONTH && (
          <Fragment>
            {Object.keys(CALENDAR_MONTHS).map(renderCalendarMonths)}
          </Fragment>
        )}
      </Styled.CalendarGrid>
    </Styled.CalendarContainer>
  );
}
